import React, { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import SearchIcon from '@mui/icons-material/Search';
import UserListModal from './UserListModal';
import ConfirmationPopup from '../../pure-components/ConfirmationPopup';
import API from '../../services/api';
import CookieManager from '../../services/CookieManager';
import { toast } from 'react-toastify';
import { ErrorToast, SuccessToast } from '../../pure-components/CustomToast';
import CorporateUserForm from './corporate-user-form';


const DEFAULT_CORPORATE = {
  corporate_name: '-',
  admin_email: '-',
  users: 0,
  projects: 0,
  licenses: 0,
  downloads: 0,
  status: '-',
};


const columns = [
  { key: 'companyName', label: 'Company Name', width: '180px' },
  { key: 'adminName', label: 'Admin Name', width: '150px' },
  { key: 'adminEmail', label: 'Admin Email', width: '220px' },
  { key: 'taxNumber', label: 'Tax Number', width: '150px' },
  { key: 'licenses', label: 'Licenses', width: '100px' },
  { key: 'renewalDate', label: 'Renewal Date', width: '150px' },
  { key: 'status', label: 'Status', width: '120px' },
  { key: 'actions', label: 'Actions', width: '120px' },
];


const CorporatePortal = () => {
  const [corporates, setCorporates] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedAdminEmail, setSelectedAdminEmail] = useState(null);
  const [selectedCompanyName, setSelectedCompanyName] = useState('');
  const [selectedAdminName, setSelectedAdminName] = useState('');
  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const itemsPerPage = 15;
  const profileData = CookieManager.getCookie('user-data')?.length
    ? JSON.parse(CookieManager.getCookie('user-data'))
    : null;


  let [showAddNewAdminForm, setShowAddNewAdminForm] = useState(false);


  const fetchAdmin = () => {
    setLoading(true);
    API.getAdminList()
      .then(res => {
        if (res.success) {
          setCorporates(Array.isArray(res.data) ? res.data : []);
          // console.log('Admin List:', res.data);
        } else {
          toast(({ closeToast }) => <ErrorToast message={res.msg} />);
        }
      })
      .catch(err => {
        setError(err.message || 'Error fetching data');
      })
      .finally(() => {
        setLoading(false);
      });
  };


  useEffect(() => {
    if (profileData?.userType === 'SUPER_ADMIN') {
      fetchAdmin();
    }
  }, []);


  const suspendAdmin = (corpId, currentStatus) => {
    const newStatus = !currentStatus;
    API.suspendAdmin(corpId, { isSuspended: newStatus })
      .then(res => {
        if (res.success) {
          setCorporates(prev =>
            prev.map(corp =>
              corp._id === corpId ? { ...corp, isSuspended: newStatus } : corp
            )
          );
          toast(({ closeToast }) => <SuccessToast message={res.msg} />);
          fetchAdmin(); // Optional: Keep for backend consistency
        } else {
          toast(({ closeToast }) => <ErrorToast message={res.msg} />);
        }
      })
      .catch(err => {
        toast(({ closeToast }) => <ErrorToast message={err.message || 'Failed to update status'} />);
      })
      .finally(() => {
        setShowConfirmPopup(false); // Close the popup after action
        setSelectedAdmin(null); // Clear selected admin
      });
  };


  const handleStatusChange = (corp) => {
    setSelectedAdmin({ _id: corp._id, isSuspended: corp.isSuspended });
    setShowConfirmPopup(true);
  };


  // Enhanced search functionality
  const filteredCorporates = corporates.filter(corp => {
    const searchLower = search.toLowerCase();
    const companyName = (corp.companyName || '-').toLowerCase();
    const adminName = (corp.first_name || '-').toLowerCase();
    const adminEmail = (corp.email || '-').toLowerCase();
    
    return (
      companyName.includes(searchLower) ||
      adminName.includes(searchLower) ||
      adminEmail.includes(searchLower)
    );
  });


  // Reset currentPage when search changes or filtered results change
  useEffect(() => {
    const totalPages = Math.ceil(filteredCorporates.length / itemsPerPage);
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1); // Reset to first page if currentPage exceeds totalPages
    }
  }, [search, filteredCorporates.length, currentPage, itemsPerPage]);


  const totalPages = Math.ceil(filteredCorporates.length / itemsPerPage);
  const paginatedCorporates = filteredCorporates.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );


  const truncateText = (text, maxLength) => {
    if (!text || text === '-') return text;
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };


  return (
    <div className="p-4 max-w-full">
      {/* Search and Add Button */}
      <div className="flex flex-row items-start sm:items-center gap-4 mb-6">
        <div className="relative sm:flex-1 min-w-[200px] max-w-md">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2">
            <SearchIcon fontSize="small" className="text-gray-400" />
          </span>
          <input
            type="text"
            placeholder="Search by company, admin name, or email..."
            className="pl-10 pr-3 h-12 border border-gray-200 rounded-md text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-200"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
<button 
  className="group flex items-center justify-center sm:justify-start gap-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 whitespace-nowrap h-12 w-12 sm:w-auto px-3 sm:px-0 transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-blue-200" 
  onClick={() => setShowAddNewAdminForm(true)}
> 
  <div className="sm:ml-6"> 
    <AddIcon fontSize="small" className="text-white" /> 
  </div> 
  <span className="tracking-wide mr-6 hidden sm:inline">Add Corporate Admin</span> 
</button>
      </div>


      {/* Responsive Table Container */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            Loading...
          </div>
        ) : error ? (
          <div className="py-12 text-center text-red-500">{error}</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#f7f8fa] border-b border-gray-200">
                    {columns.map(col => (
                      <th
                        key={col.key}
                        className="text-gray-700 font-semibold px-4 py-3 text-left whitespace-nowrap"
                        style={{ width: col.width, minWidth: col.width }}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedCorporates.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} className="text-center py-12 text-gray-500">
                        No corporates found.
                      </td>
                    </tr>
                  ) : (
                    paginatedCorporates.map((corp, idx) => {
                      const {
                        companyName = '-',
                        first_name = '-',
                        email = '-',
                        taxNumber = '-',
                        totalLicenseCount = 0,
                        renewalDate = '-',
                        isSuspended = false,
                      } = corp || {};


                      return (
                        <tr
                          key={corp._id || idx}
                          className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <td className="px-4 py-3" style={{ width: columns[0].width, minWidth: columns[0].width }}>
                            <div
                              className="font-medium text-gray-900 overflow-auto max-h-16"
                              title={companyName}
                              style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#cbd5e0 #f7fafc',
                              }}
                            >
                              {companyName}
                            </div>
                          </td>
                          <td className="px-4 py-3" style={{ width: columns[1].width, minWidth: columns[1].width }}>
                            <div
                              className="text-gray-900 overflow-auto max-h-16"
                              title={first_name}
                              style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#cbd5e0 #f7fafc',
                              }}
                            >
                              {first_name}
                            </div>
                          </td>
                          <td className="px-4 py-3" style={{ width: columns[2].width, minWidth: columns[2].width }}>
                            <div
                              className="text-gray-900 overflow-auto max-h-16"
                              title={email}
                              style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#cbd5e0 #f7fafc',
                              }}
                            >
                              {email}
                            </div>
                          </td>
                          <td className="px-4 py-3" style={{ width: columns[3].width, minWidth: columns[3].width }}>
                            <div
                              className="text-gray-900 overflow-auto max-h-16"
                              title={taxNumber}
                              style={{
                                wordBreak: 'break-word',
                                overflowWrap: 'break-word',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#cbd5e0 #f7fafc',
                              }}
                            >
                              {taxNumber}
                            </div>
                          </td>
                          <td
                            className="px-4 py-3 text-center"
                            style={{ width: columns[4].width, minWidth: columns[4].width }}
                          >
                            <div className="text-gray-900 font-medium">{totalLicenseCount}</div>
                          </td>
                          <td className="px-4 py-3" style={{ width: columns[5].width, minWidth: columns[5].width }}>
                            <div className="text-gray-900 truncate">
                              {renewalDate !== '-' ? new Date(renewalDate).toLocaleDateString('en-GB') : '-'}
                            </div>
                          </td>
                          <td className="px-4 py-3" style={{ width: columns[6].width, minWidth: columns[6].width }}>
                            <select
                              className={`w-full px-2 py-1 rounded text-xs font-medium border-0 focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all ${
                                isSuspended
                                  ? 'bg-red-100 text-red-800 focus:ring-red-200'
                                  : 'bg-green-100 text-green-800 focus:ring-green-200'
                              }`}
                              value={isSuspended ? 'Suspended' : 'Active'}
                              onChange={() => handleStatusChange(corp)}
                            >
                              <option value="Active">Active</option>
                              <option value="Suspended">Suspended</option>
                            </select>
                          </td>
                          <td className="px-4 py-3" style={{ width: columns[7].width, minWidth: columns[7].width }}>
                            <button
                              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all text-xs font-medium w-full justify-center"
                              onClick={() => {
                                setSelectedAdminEmail(corp.email);
                                setSelectedCompanyName(corp.companyName || '-');
                                setSelectedAdminName(corp.first_name || '-');
                                setUserModalOpen(true);
                              }}
                            >
                              <GroupIcon fontSize="small" />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>


            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50 gap-4">
                <div className="text-sm text-gray-700">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                  {Math.min(currentPage * itemsPerPage, filteredCorporates.length)} of {filteredCorporates.length}{' '}
                  entries (Total Pages: {totalPages})
                </div>
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>


                  <div className="flex gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }


                      return (
                        <button
                          key={pageNum}
                          className={`px-3 py-1 rounded-md border transition-colors text-sm ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white border-blue-600'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                          }`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>


                  <button
                    className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>


      {/* Confirmation Popup */}
      {showConfirmPopup && selectedAdmin ? (
        <ConfirmationPopup
          giveStyle="-5rem"
          yesAction={() => suspendAdmin(selectedAdmin._id, selectedAdmin.isSuspended)}
          cancelAction={() => {
            setShowConfirmPopup(false);
            setSelectedAdmin(null);
          }}
          title={`${selectedAdmin.isSuspended ? 'Activate' : 'Suspend'} Admin`}
          msg={`Are you sure, you want to ${selectedAdmin.isSuspended ? 'Activate' : 'Suspend'} this admin?`}
        />
      ) : null}


      {/* Modals */}
      <UserListModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        adminEmail={selectedAdminEmail}
        companyName={selectedCompanyName}
        adminName={selectedAdminName}
      />
      {
        showAddNewAdminForm &&
        <CorporateUserForm setShowAddNewUserForm={setShowAddNewAdminForm} fetchUserList={fetchAdmin} />
      }
    </div>
  );
};


export default CorporatePortal;
